import NewsletterSignup from '../components/NewsletterSignup';
import TopMovers from '../components/TopMovers';
import ProtocolSearch from '../components/ProtocolSearch';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowRight, Bell, BarChart3, Calculator } from 'lucide-react';

export default function HomePage() {
  const [stats, setStats] = useState<{ protocols: number; networks: number; dataPoints: number } | null>(null);

  useEffect(() => {
    const apiBase = (import.meta.env.VITE_API_URL || 'http://64.23.240.10/api/v1').replace('/v1', '');
    fetch(`${apiBase}/stats/public`)
      .then(r => r.json())
      .then(d => setStats(d))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-navy-dark">

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-dark opacity-50"></div>
        <div className="section-container relative z-10 text-center py-24">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-cream">Find the Highest </span>
            <span className="gradient-text-neon">Staking Yields</span>
            <span className="text-cream"> — Before Everyone Else Does</span>
          </h1>
          <p className="text-xl text-sky-blue mb-8 max-w-3xl mx-auto">
            Compare 156+ DeFi protocols in real time. Track APY changes. Get alerted the moment yields move. Free to start.
          </p>
          <div className="mb-8 px-4">
            <ProtocolSearch />
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/staking" className="btn-neon inline-flex items-center justify-center">
              Compare Staking Yields Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/calculator"
              className="bg-sky-blue/10 border border-sky-blue text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-sky-blue/20 transition-all inline-flex items-center justify-center"
            >
              <Calculator className="mr-2 w-5 h-5" />
              See How It Works
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section — 3 Cards */}
      <section className="section-container">
        <h2 className="text-4xl font-bold text-center mb-4">
          <span className="text-cream">Why Stakers Choose </span>
          <span className="gradient-text-neon">ResidualVault</span>
        </h2>
        <p className="text-sky-blue text-center mb-12 max-w-2xl mx-auto">
          The data platform serious DeFi stakers use to find, compare, and monitor yields
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="card-premium group">
            <div className="w-12 h-12 bg-gradient-cta rounded-lg flex items-center justify-center mb-4 group-hover:shadow-neon transition-all">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">Real-Time APY Tracking</h3>
            <p className="text-sky-blue">
              ResidualVault monitors 156+ staking protocols continuously — so you always know where the highest yields are. Set APY alerts and get emailed the moment a threshold is crossed.
            </p>
          </div>

          <div className="card-premium group">
            <div className="w-12 h-12 bg-gradient-cta rounded-lg flex items-center justify-center mb-4 group-hover:shadow-neon transition-all">
              <Bell className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">Side-by-Side Protocol Comparison</h3>
            <p className="text-sky-blue">
              Compare up to 5 protocols simultaneously across APY, TVL, risk profile, and 48-point historical data. Stop guessing — make data-driven staking decisions.
            </p>
          </div>

          <div className="card-premium group">
            <div className="w-12 h-12 bg-gradient-cta rounded-lg flex items-center justify-center mb-4 group-hover:shadow-neon transition-all">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">Passive Income Calculator</h3>
            <p className="text-sky-blue">
              Model your staking returns across multiple protocols and time horizons up to 10 years. See exactly how compounding works on your specific portfolio size.
            </p>
          </div>
        </div>
      </section>

      {/* Live Data Counter (Social Proof) */}
      {stats && (
        <section className="section-container">
          <div className="card-premium text-center max-w-4xl mx-auto">
            <p className="text-xl text-sky-blue">
              Currently tracking{' '}
              <span className="text-cream font-bold">{stats.protocols}</span> protocols across{' '}
              <span className="text-cream font-bold">{stats.networks}</span> blockchain networks —{' '}
              <span className="text-cream font-bold">{stats.dataPoints.toLocaleString()}</span> APY data points updated daily
            </p>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="section-container bg-gradient-dark">
        <h2 className="text-4xl font-bold text-center mb-4 text-cream">
          How It Works
        </h2>
        <p className="text-sky-blue text-center mb-12 max-w-2xl mx-auto">
          Three simple steps to find the best staking opportunities
        </p>

        <div className="max-w-4xl mx-auto">
          <div className="relative">
            <div className="absolute left-8 top-16 bottom-16 w-0.5 bg-gradient-to-b from-neon-red via-steel-blue to-sky-blue hidden md:block"></div>

            <div className="flex gap-6 mb-12">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-cta rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-neon">
                  1
                </div>
              </div>
              <div className="card-glass flex-1">
                <h3 className="text-2xl font-bold text-cream mb-2">Compare &amp; Research</h3>
                <p className="text-sky-blue">
                  Browse 156+ staking protocols. Filter by APY, risk level, and lock period. Use our calculator to project earnings over time.
                </p>
              </div>
            </div>

            <div className="flex gap-6 mb-12">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-cta rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-neon">
                  2
                </div>
              </div>
              <div className="card-glass flex-1">
                <h3 className="text-2xl font-bold text-cream mb-2">Track &amp; Monitor</h3>
                <p className="text-sky-blue">
                  Add protocols to your watchlist. Get alerts when APY rates change. Track your portfolio performance in real time.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-cta rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-neon">
                  3
                </div>
              </div>
              <div className="card-glass flex-1">
                <h3 className="text-2xl font-bold text-cream mb-2">Maximize Returns</h3>
                <p className="text-sky-blue">
                  Use our analysis tools to find the best risk-adjusted yields. Stay informed with weekly APY reports and market insights.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-container">
        <TopMovers />
        <div className="max-w-2xl mx-auto mb-16">
          <NewsletterSignup source="homepage" />
        </div>
        <div className="card-premium text-center max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">
            <span className="text-cream">Your staking returns are only as good as </span>
            <span className="gradient-text-neon">your data.</span>
          </h2>
          <p className="text-sky-blue text-lg mb-8">
            Start free. Upgrade when you're ready.
          </p>
          <Link to="/register" className="btn-neon inline-flex items-center justify-center">
            Start Comparing for Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

    </div>
  );
}
